import * as _ from "lodash";
import * as React from "react";
import { toast } from "react-toastify";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import { useHistory } from "react-router-dom";

import {
  CreateListParams,
  CreateOrderFromListParams,
  listsApi,
  UpdateListParams
} from "src/core/lists/api/lists.api";
import {
  ICurrentList,
  ListMode
} from "src/core/lists/store/lists.reducer";
import type { IStore } from "src/core/shared/store";
import { setGlobalError } from "src/core/shared/store/global/global.actions";
import { MenuContext } from "src/core/shared/hooks/menu/useMenu";
import { useDrop } from "src/core/drops/hooks";
import { IDrop } from "src/core/drops/api/drops.api";
import { IOrder } from "src/core/orders/api/orders.api";
import { tokenizeString } from "src/library/utils";
import { orderPlacedRedirectPath } from "src/core/orders/utils/order-placed-redirect";

import { IList } from "../api/lists.api";
import { initialListState } from "../store/lists.reducer";
import { setCurrentOrder } from "../../orders/store/orders.actions";
import useTerminology from "../../shared/hooks/useTerminology";
import useSetCurrentList from "./useSetCurrentList";

interface IState {
  currentList: ICurrentList;
  isCreatingList: boolean;
  isSavingList: boolean;
  isLoading: boolean;
  drop: IDrop | null;
}

interface IActions {
  setListMode: (mode: ListMode, newList?: IList) => boolean;
  refreshList: () => void;
  saveList: () => void;
  setList: (currentList: ICurrentList) => void;
  createList: (name: string) => void;
  createOrderFromList: (params: CreateOrderFromListParams) => void;
  deleteList: (id: number) => void;
}

const useList = (listId?: number, list?: IList | null, getData = false, refetchLists?: () => void): [IState, IActions] => {
  const history = useHistory();
  const [ menu ] = React.useContext(MenuContext);
  const dispatch = useDispatch();

  const {
    currentList, orderStatuses, global: {
      settings, role, masqueradeMode
    }
  } = useMappedState(({ lists, global }: IStore) => ({
    currentList: lists.currentList,
    orderStatuses: global.settings?.orderStatuses,
    role: global.role,
    global
  }));

  const preauthEnabled = !!settings?.payment?.preauthorizationsEnabled;
  const terminology = useTerminology();
  const [ getListResponse, getList ] = listsApi<IList>("GET_LIST");
  const [ createListResponse, createListRequest ] = listsApi<IList>("CREATE_LIST");
  const [ saveListResponse, saveListRequest ] = listsApi<IList>("UPDATE_LIST");
  const [ createOrderFromListResponse, createOrderFromList ] = listsApi<IOrder>("CREATE_ORDER_FROM_LIST");
  const [ deleteListResponse, deleteList ] = listsApi<{message: string}>("DELETE_LIST");
  const [ currentListState, setCurrentListState ] = React.useState<ICurrentList>(initialListState.currentList);
  const [ { drop } ] = useDrop();
  const setCurrentList = useSetCurrentList();

  const triggerReload = React.useCallback(() => {
    if (refetchLists) {
      refetchLists();
    }
  }, [ refetchLists ]);

  React.useEffect(() => {
    if (list) {
      setList({
        mode: "view",
        data: list,
        saved: true
      });
    }
  }, [ list ]);

  React.useEffect(() => {
    if (listId && getData) {
      if ((currentList.mode && currentList.data && currentList.data.id !== listId) || !currentList || currentList.mode === "view") {
        getList<number>(listId);
      }
    }
  }, [ listId ]);

  React.useEffect(() => {
    if (!list) {
      setCurrentListState(currentList);
    }
  }, [ currentList ]);

  React.useEffect(() => {
    if (deleteListResponse.data) {
      toast.success(`${terminology?.list.singular.text} was deleted successfully.`);

      if (list) {
        triggerReload();
      } else if (currentListState && role) {
        history.push(role.type === "admin" ? "/admin/lists" : "/lists");
      }
    }

    if (deleteListResponse.error) {
      toast.error(`Could not delete. ${deleteListResponse.error.message}`);
    }
  }, [
    deleteListResponse,
    currentListState,
    history,
    list,
    triggerReload
  ]);

  React.useEffect(() => {
    if (createOrderFromListResponse.data) {
      const orderId = createOrderFromListResponse.data.id;
      const isDraft = createOrderFromListResponse.data.orderStatusId === _.find(orderStatuses, status => status.settings.group === "DRAFT")?.id;

      dispatch(setCurrentOrder({
        data: createOrderFromListResponse.data,
        mode: "view",
        saved: true,
        allowedDays: null
      }));

      if (isDraft) {
        history.push(`/orders/${orderId}`, { edit: true });
      } else if (orderId && drop && role) {
        history.push(orderPlacedRedirectPath(orderId, drop, role, preauthEnabled));
      }
    }

    if (createOrderFromListResponse.error) {
      toast.error(`Could not create order from list - ${createOrderFromListResponse.error.message}`);
    }
  }, [
    createOrderFromListResponse,
    dispatch,
    drop,
    history,
    orderStatuses,
    role,
    preauthEnabled
  ]);

  const setList = React.useCallback(
    (newCurrentList: ICurrentList) => {
      if (!listId || (newCurrentList.data && newCurrentList.data.id === listId)) {
        if (!newCurrentList.data) {
          setCurrentListState({
            ...currentListState,
            ...newCurrentList
          });

          if (!list) {
            setCurrentList({
              ...currentListState,
              ...newCurrentList
            });
          }

          return;
        }

        if (list) {
          setCurrentListState({ ...newCurrentList });
        } else {
          setCurrentList({ ...newCurrentList });
        }
      }
    },
    [
      currentListState,
      list,
      listId
    ]
  );

  React.useEffect(() => {
    if (getListResponse.data) {
      if (masqueradeMode?.active && drop?.id !== getListResponse.data.branchId) {
        history.push("/dashboard");

        return;
      }

      setList({
        data: getListResponse.data,
        mode: "view",
        saved: true
      });
    }

    if (getListResponse.error) {
      setCurrentList(null);
      dispatch(setGlobalError(getListResponse.error));
    }
  }, [
    getListResponse,
    masqueradeMode?.active,
    history,
    drop,
    setCurrentList,
    dispatch
  ]);

  React.useEffect(() => {
    if (createListResponse.data) {
      toast.success(`Successfully created a new ${settings?.terminology ? tokenizeString("%LIST%", settings.terminology) : "list"}`);

      setList({
        data: createListResponse.data,
        mode: "edit",
        saved: true
      });

      if (!menu.context || menu.context.length === 0 || menu.context[ 0 ].type !== "LIST_PRODUCT_SELECTION") {
        history.push("/products");
      }
    }

    if (createListResponse.error) {
      toast.error(`There was an error whilst creating your list: ${createListResponse.error.message}. Please try again.`);
    }
  }, [ createListResponse ]);

  const createList = React.useCallback(
    (name: string) => {
      if (drop) {
        createListRequest<CreateListParams>({
          branchId: drop.id,
          name,
          listLines: []
        });
      }
    },
    [ drop, createListRequest ]
  );

  const refreshList = React.useCallback(() => {
    if (currentList.data && !list) {
      getList(currentList.data.id);
    } else if (list) {
      triggerReload();
    }
  }, [
    triggerReload,
    getList,
    currentList,
    list
  ]);

  const setListMode = React.useCallback(
    (mode: ListMode, newList?: IList): boolean => {
      if (newList) {
        setList({
          data: newList,
          mode,
          saved: true
        });

        return true;
      }

      if (currentList) {
        setList({
          data: currentList.data,
          mode,
          saved: mode === "view" ? true : currentList.saved
        });

        return true;
      }

      return false;
    },
    [ currentList ]
  );

  const saveList = React.useCallback(() => {
    if (currentList.data && !currentList.saved && drop) {
      const toUpdate = { ...currentList.data };

      toUpdate.listLines = toUpdate.listLines.map(listLine => {
        const line = { ...listLine };

        //@ts-ignore
        delete line.product;

        return line;
      });
      delete toUpdate.schedule;
      saveListRequest<UpdateListParams>(toUpdate);
    }
  }, [
    currentList,
    drop,
    saveListRequest,
    setListMode
  ]);

  React.useEffect(() => {
    if (saveListResponse.error) {
      setListMode("edit");
      toast.error(saveListResponse.error.message);
    } else if (saveListResponse.data) {
      setList({
        data: saveListResponse.data,
        saved: true,
        mode: "view"
      });

      if (saveListResponse.data.schedule) {
        toast.success("List saved successfully, you're standing orders are being validated, we will let you know when validation is complete!");
      }
    }
  }, [ saveListResponse ]);

  // make sure we return correct state based on state in use
  const currentListResponse = React.useMemo(() => {
    if (list && currentListState?.data?.id === list.id) {
      return currentListState;
    }

    if (listId && currentList?.data?.id === listId) {
      return currentList;
    }

    if (!listId && !list) {
      return currentList;
    }

    return {
      data: null,
      mode: "view",
      saved: true
    } as ICurrentList;
  }, [
    currentList,
    currentListState,
    list,
    listId
  ]);

  return [
    {
      currentList: currentListResponse,
      isCreatingList: createListResponse.pending,
      isSavingList: saveListResponse.pending,
      isLoading: getListResponse.pending,
      drop
    },
    {
      setListMode,
      saveList,
      refreshList,
      setList,
      createList,
      createOrderFromList,
      deleteList
    }
  ];
};

export default useList;
